/**
 * DataDisplay.tsx
 * Data Display components category page - Kbd, Progress, Table, Avatar, etc.
 * 
 * Showcases all data display and presentation-related HeroUI components with organized layout
 */

import React from 'react';
import { useNavigate } from 'react-router-dom';
import { PlaygroundPageLayout } from '../shared';
import { Card, CardHeader, CardBody, CardFooter } from '@enfomentis/ui';
import { Badge } from '@enfomentis/ui';
import { Chip } from '@enfomentis/ui';
import { Kbd } from '@enfomentis/ui';
import { Database, Code, Image, User } from 'lucide-react';

export const DataDisplay: React.FC = () => {
  const navigate = useNavigate();

  const dataDisplayComponents = [
    {
      name: 'Kbd',
      description: 'Display keyboard keys and shortcuts with professional styling',
      status: 'available',
      heroUIBase: 'Kbd',
      route: '/playground/components/data-display/kbd',
      icon: Code,
      preview: (
        <div className="text-xs space-y-2">
          <div className="flex items-center justify-center gap-2">
            <Kbd keys={["command"]}>⌘</Kbd>
            <span className="text-text-tertiary">+</span>
            <Kbd keys={["shift"]}>K</Kbd>
          </div>
          <div className="text-text-tertiary text-center">Keyboard Shortcuts</div>
        </div>
      )
    },
    {
      name: 'Avatar',
      description: 'User profile images and placeholder avatars',
      status: 'not-started',
      heroUIBase: 'Avatar',
      route: '/playground/components/data-display/avatar',
      icon: User,
      preview: (
        <div className="text-xs space-y-2">
          <div className="flex justify-center">
            <div className="w-8 h-8 bg-primary-500 rounded-full flex items-center justify-center text-white text-xs font-medium">
              JD
            </div>
          </div>
          <div className="text-text-tertiary text-center">User Avatar</div>
        </div>
      )
    },
    {
      name: 'Table',
      description: 'Basic data tables with sorting and selection (for simple data)',
      status: 'available',
      heroUIBase: 'Table',
      route: '/playground/components/data-display/table',
      icon: Database,
      preview: (
        <div className="text-xs space-y-2">
          <div className="border border-border rounded overflow-hidden">
            <div className="bg-surface px-2 py-1 border-b border-border text-text-secondary font-medium">Header</div>
            <div className="px-2 py-1 text-text-primary">Row 1</div>
            <div className="px-2 py-1 text-text-primary bg-surface/50">Row 2</div>
          </div>
          <div className="text-text-tertiary text-center">Data Tables</div>
        </div>
      )
    },
    {
      name: 'Code',
      description: 'Code blocks and inline code snippets with syntax highlighting',
      status: 'available',
      heroUIBase: 'Code',
      route: '/playground/components/data-display/code',
      icon: Code,
      preview: (
        <div className="text-xs space-y-2">
          <div className="bg-default-100 dark:bg-default-800 rounded px-2 py-1 font-mono text-primary">
            console.log()
          </div>
          <div className="text-text-tertiary text-center">Code Display</div>
        </div>
      )
    },
    {
      name: 'Image',
      description: 'Responsive images with loading states and fallbacks',
      status: 'available',
      heroUIBase: 'Image',
      route: '/playground/components/data-display/image',
      icon: Image,
      preview: (
        <div className="text-xs space-y-2">
          <div className="w-12 h-8 bg-default-200 rounded flex items-center justify-center">
            <Image className="w-4 h-4 text-text-tertiary" />
          </div>
          <div className="text-text-tertiary text-center">Images</div>
        </div>
      )
    },
    {
      name: 'Snippet',
      description: 'Copyable code snippets with copy-to-clipboard functionality',
      status: 'available',
      heroUIBase: 'Snippet',
      route: '/playground/components/data-display/snippet',
      icon: Code,
      preview: (
        <div className="text-xs space-y-2">
          <div className="bg-default-100 dark:bg-default-800 rounded px-2 py-1 font-mono text-primary flex items-center gap-1">
            <span>npm install</span>
            <div className="w-3 h-3 bg-text-tertiary rounded"></div>
          </div>
          <div className="text-text-tertiary text-center">Code Snippets</div>
        </div>
      )
    }
  ];

  const handleComponentClick = (route: string) => {
    navigate(route);
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-6">
        
        {/* Category Header */}
        <div className="text-center px-4">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Database className="w-6 h-6 md:w-8 md:h-8 text-primary" />
            <h1 className="text-2xl md:text-3xl font-bold text-text-primary">Data Display</h1>
          </div>
          <p className="text-base md:text-lg text-text-secondary max-w-2xl mx-auto leading-relaxed">
            Components for displaying data, content, and information in organized, accessible formats.
            Present complex information clearly with tables, keyboards, and content displays.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-2 mt-4">
            <Badge color="success" variant="flat">
              {dataDisplayComponents.filter(c => c.status === 'available').length} available
            </Badge>
            <Badge color="warning" variant="flat">
              {dataDisplayComponents.filter(c => c.status === 'not-started').length} planned
            </Badge>
            <Chip size="sm" color="primary" variant="dot">HeroUI Based</Chip>
          </div>
        </div>

        {/* Components Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 px-4 md:px-0">
          {dataDisplayComponents.map((component) => {
            const IconComponent = component.icon;
            const isAvailable = component.status === 'available';
            
            return (
              <Card
                key={component.name}
                isPressable={isAvailable}
                {...(isAvailable && { onPress: () => handleComponentClick(component.route) })}
                className={`group transition-all duration-200 ${
                  isAvailable 
                    ? 'hover:shadow-lg cursor-pointer' 
                    : 'opacity-75 cursor-not-allowed'
                }`}
              >
                <CardHeader>
                  <div className="flex items-start justify-between w-full gap-3">
                    <div className="flex items-center gap-2 min-w-0 flex-1">
                      <IconComponent className="w-5 h-5 text-primary flex-shrink-0" />
                      <h3 className="font-semibold text-primary text-base md:text-lg truncate">{component.name}</h3>
                    </div>
                    <Chip 
                      color={
                        component.status === 'available' ? 'success' : 
                        component.status === 'not-started' ? 'default' : 'warning'
                      }
                      size="sm"
                      variant="flat"
                      className="flex-shrink-0"
                    >
                      {component.status === 'available' ? 'available' : 
                       component.status === 'not-started' ? 'planned' : component.status}
                    </Chip>
                  </div>
                </CardHeader>
                
                <CardBody className="space-y-4">
                  <p className="text-text-secondary">{component.description}</p>
                  
                  {/* Component Preview */}
                  <div className="p-4 bg-background rounded-lg border border-border">
                    {component.preview}
                  </div>
                  
                  <div className="flex items-center gap-2">
                    <Chip size="sm" variant="bordered" color="primary">
                      {component.heroUIBase}
                    </Chip>
                  </div>
                </CardBody>
                
                <CardFooter>
                  <div className={`w-full text-center py-1 px-3 rounded-md border transition-colors ${
                    isAvailable 
                      ? 'border-primary text-primary group-hover:bg-primary group-hover:text-primary-foreground' 
                      : 'border-default-200 text-default-400'
                  }`}>
                    {isAvailable ? 'View Examples & Documentation →' : 'Coming Soon'}
                  </div>
                </CardFooter>
              </Card>
            );
          })}
        </div>

        {/* Category Info */}
        <Card className="bg-surface">
          <CardBody>
            <div className="text-center space-y-4">
              <h3 className="text-lg font-semibold text-text-primary">About Data Display Components</h3>
              <p className="text-text-secondary max-w-3xl mx-auto">
                Data display components help present content and information in clear, organized, and accessible formats. 
                From keyboard shortcuts to data tables, these components ensure users can easily understand and 
                interact with your application's content and data.
              </p>
              <div className="flex flex-wrap items-center justify-center gap-2">
                <Chip size="sm" variant="flat" color="primary">Content Presentation</Chip>
                <Chip size="sm" variant="flat" color="secondary">Data Visualization</Chip>
                <Chip size="sm" variant="flat" color="success">User Interface</Chip>
                <Chip size="sm" variant="flat" color="warning">Information Display</Chip>
              </div>
              <div className="mt-4 p-3 bg-default-100/50 dark:bg-default-800/50 rounded-lg">
                <p className="text-sm text-text-tertiary">
                  <strong>Note:</strong> For complex trading data tables, we use AG Grid (see Data Visualization playground). 
                  The HeroUI Table component is intended for simple data presentation needs.
                </p>
              </div>
            </div>
          </CardBody>
        </Card>

      </div>
    </PlaygroundPageLayout>
  );
};